import { isNilOrEmpty } from "ramda-adjunct";
import { DelimiterSchema, SchemaType, SectionSchema } from "./types";

/**
 * Type guard for delimiter schema
 */
export function isDelimiter(item: DelimiterSchema | SectionSchema): item is DelimiterSchema {
  return item.type === SchemaType.DELIMITER;
}

/**
 * Type guard for section schema
 */
export function isSection(item: DelimiterSchema | SectionSchema): item is SectionSchema {
  return item.type === SchemaType.SECTION;
}

type Schema = (SectionSchema | DelimiterSchema)[];
type ParsedFields = Record<string, string>;

/**
 * Parses a bank number string into fields based on a schema definition
 */
export const parseStringToFields = (schema: Schema, bankNumber: string): ParsedFields => {
  // Step 1: Extract and split by delimiters
  const delimitedParts = splitByDelimiters(schema, bankNumber);
  // Step 2: Clean parts by removing non-alphanumeric characters
  const cleanedParts = delimitedParts.map(sanitizePart);

  // Step 3: Extract field values based on schema sections
  const fieldValues = extractFieldValues(schema, cleanedParts);

  // Step 4: Map field values to schema field names
  return mapFieldsToSchema(schema, fieldValues);
};

/**
 * Extracts delimiters from schema and splits the bank number accordingly
 */
const splitByDelimiters = (schema: Schema, bankNumber: string): string[] => {
  const delimiters = extractDelimiters(schema);

  return delimiters.reduce(
    (parts, delimiter) => {
      const lastPart = parts[parts.length - 1];

      if (!lastPart || isNilOrEmpty(lastPart)) {
        return parts;
      }

      const delimiterIndex = lastPart.indexOf(delimiter);
      if (delimiterIndex === -1) {
        return parts;
      }

      return [...parts.slice(0, -1), lastPart.substring(0, delimiterIndex), lastPart.substring(delimiterIndex + 1)];
    },
    [bankNumber]
  );
};

/**
 * Extracts valid delimiters from schema
 */
const extractDelimiters = (schema: Schema): string[] =>
  schema.filter(({ type, title }) => type === SchemaType.DELIMITER && title !== "").map(({ title }) => title);

/**
 * Removes non-alphanumeric characters from a string part
 */
const sanitizePart = (part: string): string => part.replace(/[^a-zA-Z0-9]/g, "");

/**
 * Extracts field values from cleaned parts based on schema sections
 */
const extractFieldValues = (schema: Schema, cleanedParts: string[]): string[] => {
  const parts = [...cleanedParts]; // Create a copy to avoid mutation
  const sections = getSectionsOnly(schema);

  return sections.reduceRight((fieldValues, section) => {
    const part = parts.pop();

    if (!part) {
      return fieldValues;
    }

    if (part.length <= section.size) {
      return [part, ...fieldValues];
    }

    // Handle overflow: split the part to fit section size
    const { overflow, fitted } = splitPartForSection(part, section.size);
    redistributeOverflow(parts, overflow);

    return [fitted, ...fieldValues];
  }, [] as string[]);
};

/**
 * Splits a part into overflow and fitted portions based on section size
 */
const splitPartForSection = (part: string, sectionSize: number) => ({
  overflow: part.substring(0, part.length - sectionSize),
  fitted: part.substring(part.length - sectionSize),
});

/**
 * Redistributes overflow text back to the parts array
 */
const redistributeOverflow = (parts: string[], overflow: string): void => {
  if (parts.length === 0) {
    parts.push(overflow);
  } else {
    parts[parts.length - 1] += overflow;
  }
};

/**
 * Filters schema to get only section items (excluding delimiters)
 */
const getSectionsOnly = (schema: Schema): SectionSchema[] =>
  schema.filter((item): item is SectionSchema => item.type !== SchemaType.DELIMITER);

/**
 * Maps extracted field values to their corresponding schema field names
 */
const mapFieldsToSchema = (schema: Schema, fieldValues: string[]): ParsedFields => {
  const values = [...fieldValues]; // Create a copy to avoid mutation

  return schema.reduceRight((result, section) => {
    if (section.type === SchemaType.DELIMITER) {
      return result;
    }

    return {
      ...result,
      [section.name]: values.pop() ?? "",
    };
  }, {} as ParsedFields);
};
